import {
  differenceInHours,
  differenceInMinutes,
  differenceInSeconds,
  isToday,
  isYesterday,
  parseISO,
} from 'date-fns';
import i18next from 'i18next';
import { SUPPORTED_LANGUAGE } from '@/i18n/supportedLanguages';
import { formatIsoDateTimeToTimeOnly, formatIsoDateTimeToTimestamp } from './dateFormatting';

const JUST_NOW_THRESHOLD_SECONDS = 60;
const MINUTES_PER_HOUR = 60;

function resolveRelativeTimeLocale(): string {
  if (i18next.language === SUPPORTED_LANGUAGE.polish) {
    return SUPPORTED_LANGUAGE.polish;
  }
  return SUPPORTED_LANGUAGE.english;
}

function capitalizeFirstLetter(value: string): string {
  if (value.length === 0) {
    return value;
  }
  return value.charAt(0).toUpperCase() + value.slice(1);
}

export function formatRelativeTime(isoDateTime: string): string {
  const targetDate = parseISO(isoDateTime);
  const referenceDate = new Date();
  const secondsElapsed = differenceInSeconds(referenceDate, targetDate);

  if (secondsElapsed < JUST_NOW_THRESHOLD_SECONDS) {
    return i18next.t('notifications:relativeTime.justNow');
  }

  const relativeTimeFormatter = new Intl.RelativeTimeFormat(resolveRelativeTimeLocale(), {
    numeric: 'auto',
    style: 'long',
  });

  if (isToday(targetDate) === true) {
    const minutesElapsed = differenceInMinutes(referenceDate, targetDate);
    if (minutesElapsed < MINUTES_PER_HOUR) {
      return relativeTimeFormatter.format(-minutesElapsed, 'minute');
    }
    const hoursElapsed = differenceInHours(referenceDate, targetDate);
    return relativeTimeFormatter.format(-hoursElapsed, 'hour');
  }

  if (isYesterday(targetDate) === true) {
    const yesterdayLabel = capitalizeFirstLetter(relativeTimeFormatter.format(-1, 'day'));
    return yesterdayLabel + ', ' + formatIsoDateTimeToTimeOnly(isoDateTime);
  }

  return formatIsoDateTimeToTimestamp(isoDateTime);
}
